import { useReactiveVar } from '@apollo/client';
import {
  fetchDevices,
  fetchMFAPreference,
  FetchMFAPreferenceOutput,
  fetchUserAttributes,
  forgetDevice,
  resetPassword,
  updateMFAPreference
} from '@aws-amplify/auth';
import { QrCodeOutlined } from '@mui/icons-material';
import { Box, Button, Divider, ListItemText, Paper, Stack, Typography } from '@mui/material';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useCallback, useEffect, useState, type FC } from 'react';
import { Navigate } from 'react-router';
import { authVar } from 'state/vars';
import { BreadcrumbMeta } from 'v2/components/Breadcrumb/Breadcrumb';
import useModalContext from 'v2/components/Modal/useModalContext';
import useSnackbarContext from 'v2/components/Snackbar/useSnackbarContext';
import PageLayout from 'v2/layout/PageLayout/PageLayout';
import { isSecurityEnabled } from 'v2/utils/isSecurityEnabled';
import TwoFactorSetupModal from './components/TwoFactorSetupModal';

const AccountSettings: FC = () => {
  const { translation } = useTranslation();
  const { user } = useReactiveVar(authVar);
  const { openModal } = useModalContext();
  const { showSnackbar } = useSnackbarContext();
  const [mfaPreference, setMfaPreference] = useState<FetchMFAPreferenceOutput | undefined>();
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [isDisabling2FA, setIsDisabling2FA] = useState(false);

  const securityEnabled = isSecurityEnabled(user);

  const fetchPreference = useCallback(async () => {
    try {
      const pref = await fetchMFAPreference();
      setMfaPreference(pref);
    } catch (err) {
      console.error(err);
    }
  }, []);

  useEffect(() => {
    if (securityEnabled) fetchPreference();
  }, [securityEnabled, fetchPreference]);

  const handleResetPassword = async () => {
    setIsResettingPassword(true);
    try {
      const userAttributes = await fetchUserAttributes();
      if (userAttributes.email) {
        await resetPassword({ username: userAttributes.email });
        showSnackbar(translation.check_your_email, { severity: 'success' });
      }
    } catch (err) {
      showSnackbar(translation.error_while_resetting_password, { severity: 'error' });
    } finally {
      setIsResettingPassword(false);
    }
  };

  const handleDisable2FA = async () => {
    setIsDisabling2FA(true);
    try {
      const devices = await fetchDevices();
      await Promise.all(devices.map((device) => forgetDevice({ device })));
      await updateMFAPreference({ totp: 'DISABLED' });
      await fetchPreference();
    } catch (err) {
      showSnackbar(translation.error_disabling_2fa, { severity: 'error' });
    } finally {
      setIsDisabling2FA(false);
    }
  };

  const handleEnable2FA = () => {
    openModal(<TwoFactorSetupModal onComplete={fetchPreference} />, { maxWidth: 800 });
  };

  const isTotpEnabled = !!mfaPreference?.enabled;

  if (!securityEnabled) {
    return <Navigate to="/" replace />;
  }

  return (
    <PageLayout>
      {user?.isAdmin && <BreadcrumbMeta breadcrumb={{ title: translation.account_settings, crumbs: [] }} />}
      <Stack gap={3}>
        <Stack gap={2}>
          <Typography variant="h6">{translation.password}</Typography>
          <Box>
            <Button
              id="reset-password-button"
              variant="outlined"
              loading={isResettingPassword}
              onClick={handleResetPassword}
            >
              {translation.reset_password}
            </Button>
          </Box>
        </Stack>
        <Divider />
        <Stack gap={2}>
          <Typography variant="h6">{translation.two_factor_authentication}</Typography>
          {isTotpEnabled ? (
            <Stack gap={2}>
              <Paper sx={{ p: 2 }}>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <QrCodeOutlined />
                  <ListItemText
                    primary={translation.authenticator_app}
                    secondary={translation.two_factor_authentication_app_enabled}
                  />
                </Stack>
              </Paper>
              <Box>
                <Button
                  id="disable-two-factor-authentication-button"
                  color="error"
                  variant="outlined"
                  loading={isDisabling2FA}
                  onClick={handleDisable2FA}
                >
                  {translation.disable}
                </Button>
              </Box>
            </Stack>
          ) : (
            <Stack gap={1}>
              <Typography>{translation.two_factor_authentication_recommendation}</Typography>
              <Box>
                <Button id="enable-two-factor-authentication-button" variant="outlined" onClick={handleEnable2FA}>
                  {translation.enable_two_factor_authentication}
                </Button>
              </Box>
            </Stack>
          )}
        </Stack>
      </Stack>
    </PageLayout>
  );
};

export default AccountSettings;
